import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { BehaviorSubject, Subscription } from 'rxjs';
import { TwoNPlugin, CallEvent, TwoNNotificationEvent } from '@Vessel-Tech/2n-intercom';
import { CredentialsService, TwoNCredentials } from './credentials.service';
import { TwoNBleService } from './two-n-ble.service';
import { AuthService } from '../auth/auth.service';
import { DynamoDBService } from './dynamodb.service';
import { Device } from '@capacitor/device';
import { AnalyticsService } from './analytics.service';

export interface LoginStatus {
  isLoggedIn: boolean;
  state: string;
  deviceId?: string;
  userType?: string;
}

export interface TwoNConnectionStatus {
  isConnected: boolean;
  deviceId: string | null;
  error: string | null;
}

// New connection state types for explicit state management
export type TwoNConnectionState =
  | 'disconnected'    // Not connected
  | 'connecting'      // Connection in progress
  | 'connected'       // Successfully connected
  | 'error'           // Connection failed
  | 'reconnecting';   // Auto-reconnect in progress

export interface TwoNConnectionInfo {
  state: TwoNConnectionState;
  deviceId?: string;
  error?: string;
  lastUpdated: number;
}

interface TwoNLoginResponse {
  success: boolean;
  message: string;
  username?: string;
  deviceId?: string;
  deviceName?: string;
  deviceCount?: number;
}

interface TwoNLoginStatus {
  isLoggedIn: boolean;
  state: string;
  deviceId?: string;
  userType?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TwoNService {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private callEventSubject = new BehaviorSubject<CallEvent | null>(null);
  private notificationEventSubject = new BehaviorSubject<TwoNNotificationEvent | null>(null);
  private deviceIdSubject = new BehaviorSubject<string | undefined>(undefined);

  // New connection state subject for explicit state management
  private connectionStateSubject = new BehaviorSubject<TwoNConnectionInfo>({
    state: 'disconnected',
    lastUpdated: Date.now()
  });

  private callEventListener: any;
  private notificationEventListener: any;
  private isInitializing = false;
  private isFullyInitialized = false;
  private isCredentialsStoring = false;
  private isListenersInitialized = false;

  // Track login recovery attempts to prevent loops
  private isRecoveryInProgress = false;
  private lastRecoveryAttempt = 0;
  private readonly RECOVERY_COOLDOWN_MS = 10000; // 10 second cooldown
  private recoveryAttemptCount = 0;
  private readonly MAX_RECOVERY_ATTEMPTS = 3;

  // Platform compatibility - checked once at startup
  private isPlatformCompatible = false;
  private platformCheckCompleted = false;

  // Connection health check
  private healthCheckInterval: any = null;
  private readonly HEALTH_CHECK_INTERVAL_MS = 60000; // 1 minute

  isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  callEvent$ = this.callEventSubject.asObservable();
  notificationEvent$ = this.notificationEventSubject.asObservable();
  deviceId$ = this.deviceIdSubject.asObservable();
  connectionState$ = this.connectionStateSubject.asObservable();

  constructor(
    private platform: Platform,
    private credentialsService: CredentialsService,
    private bleService: TwoNBleService,
    private authService: AuthService,
    private dynamoDBService: DynamoDBService,
    private analyticsService: AnalyticsService
  ) {
    // console.log('2N Service constructed - checking platform compatibility...');
    
    // Check platform compatibility once at startup
    this.checkPlatformCompatibilityOnce().then(() => {
      if (this.isPlatformCompatible) {
        // console.log('2N Service - platform is compatible, will initialize after authentication');
        this.setupBasicEventListeners();
        this.setupAppLifecycleHandling();
      } else {
        // console.log('2N Service - platform is not compatible (web/simulator), service disabled');
      }
    }).catch(error => {
      console.warn('2N Service - error checking platform compatibility:', error);
      // console.log('2N Service - assuming incompatible platform, service disabled');
      this.isPlatformCompatible = false;
      this.platformCheckCompleted = true;
    });
  }

  /**
   * Fetches 2N credentials with DynamoDB as primary source and secure storage as fallback.
   * This consolidates the credential fetching logic that was previously duplicated.
   * @param context - Optional context string for logging (e.g., 'auto-login', 'recovery')
   * @returns TwoNCredentials or null if not found
   */
  private async fetchCredentialsWithFallback(context?: string): Promise<TwoNCredentials | null> {
    const logPrefix = context ? `[${context}] ` : '';

    // First try to get fresh credentials from DynamoDB
    try {
      const { twoNCredentials } = await this.dynamoDBService.getResidentWithCredentials();
      if (twoNCredentials) {
        // Store these credentials for future use
        await this.safeStoreTwoNCredentials(twoNCredentials);
        return twoNCredentials;
      }
    } catch (error) {
      console.error(`${logPrefix}Error retrieving credentials from DynamoDB:`, error);
    }

    // If no credentials from DynamoDB, try to get from secure storage
    const credentials = await this.credentialsService.getTwoNCredentials();
    return credentials;
  }

  /**
   * Centralized method to update connection state
   * Keeps legacy observables in sync for backward compatibility
   */
  private updateConnectionState(
    state: TwoNConnectionState,
    deviceId?: string,
    error?: string
  ): void {
    const info: TwoNConnectionInfo = {
      state,
      deviceId,
      error,
      lastUpdated: Date.now()
    };
    console.log('[2N] Connection state updated:', state, deviceId ? `(deviceId: ${deviceId})` : '', error ? `(error: ${error})` : '');
    this.connectionStateSubject.next(info);

    // Keep legacy observables in sync for backward compatibility
    this.isAuthenticatedSubject.next(state === 'connected');
    this.deviceIdSubject.next(state === 'connected' ? deviceId : undefined);
  }

  /**
   * Start periodic health check for connection status
   * Called after initialization - monitors connection and attempts recovery when disconnected
   */
  startConnectionHealthCheck(): void {
    if (this.healthCheckInterval) return;

    console.log('[2N] Starting connection health check');
    this.healthCheckInterval = setInterval(async () => {
      const currentState = this.connectionStateSubject.getValue();

      if (currentState.state === 'connected') {
        // Verify connection is still valid
        const status = await this.checkLoginStatus();
        if (!status.isLoggedIn) {
          console.warn('[2N] Health check detected disconnection, attempting recovery');
          this.handleInvalidLogin();
        }
      } else if (currentState.state === 'disconnected' || currentState.state === 'error') {
        // Attempt to establish connection if not connected
        console.log('[2N] Health check: not connected, attempting to connect...');
        await this.autoLoginWithDeviceCredentials();
      }
      // Skip if 'connecting' or 'reconnecting' - operation already in progress
    }, this.HEALTH_CHECK_INTERVAL_MS);
  }

  /**
   * Stop the connection health check
   */
  stopConnectionHealthCheck(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
      console.log('[2N] Stopped connection health check');
    }
  }

  /**
   * Check platform compatibility once at startup and cache the result
   */
  private async checkPlatformCompatibilityOnce(): Promise<void> {
    if (this.platformCheckCompleted) {
      return;
    }

    try {
      // First check: Must be running on Capacitor (not web)
      if (!this.platform.is('capacitor')) {
        // console.log('2N Service - Running on web platform, 2N services not available');
        this.isPlatformCompatible = false;
        this.platformCheckCompleted = true;
        return;
      }

      // Second check: Must be iOS or Android
      if (!this.platform.is('ios') && !this.platform.is('android')) {
        // console.log('2N Service - Platform is not iOS or Android, 2N services not available');
        this.isPlatformCompatible = false;
        this.platformCheckCompleted = true;
        return;
      }

      // Third check: Must not be running on simulator/emulator
      try {
        const deviceInfo = await Device.getInfo();
        if (deviceInfo.isVirtual) {
          // console.log('2N Service - Running on simulator/emulator, 2N services not available');
          // console.log('2N Service - Device info:', {
          //   platform: deviceInfo.platform,
          //   model: deviceInfo.model,
          //   isVirtual: deviceInfo.isVirtual
          // });
          this.isPlatformCompatible = false;
        } else {
          // console.log('2N Service - Running on real device, 2N services available');
          // console.log('2N Service - Device info:', {
          //   platform: deviceInfo.platform,
          //   model: deviceInfo.model,
          //   isVirtual: deviceInfo.isVirtual
          // });
          this.isPlatformCompatible = true;
        }
      } catch (deviceError) {
        console.error('2N Service - Error getting device info:', deviceError);
        // If we can't get device info, assume it's not compatible for safety
        this.isPlatformCompatible = false;
      }
    } catch (error) {
      console.error('2N Service - Error in platform compatibility check:', error);
      this.isPlatformCompatible = false;
    }

    this.platformCheckCompleted = true;
  }

  /**
   * Check if 2N services are available on the current platform
   * Returns the cached result from startup check
   */
  public isPlatformSupported(): boolean {
    return this.isPlatformCompatible;
  }

  /**
   * Initialize 2N services after authentication is complete
   * This should be called by the app once the user has successfully authenticated
   */
  public async initializeAfterAuth(): Promise<boolean> {
    // Wait for platform check to complete (max 5 seconds)
    const maxWait = 5000;
    const startTime = Date.now();
    while (!this.platformCheckCompleted && (Date.now() - startTime) < maxWait) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    if (!this.isPlatformCompatible) {
      console.log('2N Service - Platform not compatible, skipping initialization');
      return false;
    }

    if (this.isFullyInitialized) {
      console.log('2N services already fully initialized');
      return true;
    }

    console.log('Starting post-authentication 2N initialization...');

    try {
      const success = await this.initializeInBackground();
      this.isFullyInitialized = success;

      // If initial connection failed, schedule a retry after 5 seconds
      if (!success) {
        console.log('[2N] Initial connection failed, scheduling retry...');
        setTimeout(() => {
          if (this.connectionStateSubject.getValue().state !== 'connected') {
            console.log('[2N] Retrying connection...');
            this.autoLoginWithDeviceCredentials();
          }
        }, 5000);
      }

      return success;
    } catch (error) {
      console.error('Failed to initialize 2N services after auth:', error);
      return false;
    }
  }

  /**
   * Setup minimal event listeners that don't require full initialization
   * This allows receiving incoming calls even before full initialization
   */
  private setupBasicEventListeners() {
    if (!this.isPlatformCompatible) {
      return;
    }

    // Only set up listeners for incoming calls and critical events
    // Full listener setup happens during initializeInBackground
    // console.log('Setting up basic 2N event listeners for incoming calls');

    // Set up minimal listeners in the background to avoid blocking startup
    setTimeout(() => {
      this.setupIncomingCallListener().catch(error => {
        console.warn('Failed to setup basic call listener:', error);
      });

      // Also listen for app URL open events (for notification tap deep linking)
      this.setupAppUrlOpenListener().catch(error => {
        console.warn('Failed to setup app URL open listener:', error);
      });
    }, 100);
  }

  /**
   * Set up app lifecycle handling to sync JS connection state on foreground resume.
   * Mirrors the pattern in mqtt-connector.service.ts.
   */
  private setupAppLifecycleHandling() {
    if (!this.isPlatformCompatible) {
      return;
    }

    // Use dynamic import for Capacitor App plugin (same pattern as setupAppUrlOpenListener)
    import('@capacitor/app').then(({ App }) => {
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          this.handleAppForeground();
        }
      });
    }).catch(error => {
      console.warn('[2N] Failed to setup appStateChange listener:', error);
    });

    this.platform.resume.subscribe(() => {
      this.handleAppForeground();
    });
  }

  /**
   * Handle app coming to foreground - sync JS connection state with native plugin.
   * The native plugin (TwoNPluginCore.swift) already reconnects SIP on foreground,
   * but the JS connectionStateSubject stays stale unless we explicitly check.
   */
  private async handleAppForeground() {
    if (!this.isPlatformCompatible) {
      return;
    }

    console.log('[2N] App coming to foreground - syncing connection state');

    try {
      // autoLoginWithDeviceCredentials already handles all cases:
      // - Not logged in → fetches credentials and logs in
      // - Logged in with correct deviceId → returns early
      // - Logged in with stale deviceId → forces re-login with DynamoDB credentials
      await this.autoLoginWithDeviceCredentials();
    } catch (error) {
      console.warn('[2N] Error during foreground sync:', error);
    }
  }

  /**
   * Listen for app URL open events to handle notification tap deep linking
   */
  private async setupAppUrlOpenListener(): Promise<void> {
    try {
      const { App } = await import('@capacitor/app');

      App.addListener('appUrlOpen', async (data) => {
        console.log('[2N] App opened with URL:', data.url);

        // Check if this is an intercom-related URL
        if (data.url.includes('intercom') || data.url.includes('call')) {
          console.log('[2N] Intercom URL detected, reconnecting and navigating...');

          // Ensure we're connected to 2N
          if (!this.isFullyInitialized) {
            await this.initializeAfterAuth();
          }

          // Trigger auto-login to ensure SIP connection
          await this.autoLoginWithDeviceCredentials();

          // Navigate to intercom page
          this.notificationEventSubject.next({
            type: 'intercom',
            timestamp: Date.now(),
            reason: 'app_url_open'
          });
        }
      });

      console.log('[2N] App URL open listener setup complete');
    } catch (error) {
      console.warn('[2N] Failed to setup app URL open listener:', error);
    }
  }

  /**
   * Setup only the incoming call listener without full initialization
   */
  private async setupIncomingCallListener() {
    if (!this.isPlatformCompatible) {
      return;
    }

    try {
      // Import the plugin dynamically to avoid blocking startup
      const { TwoNPlugin } = await import('@Vessel-Tech/2n-intercom');
      
      // Only listen for direct navigation events here - call events are handled in initializeListeners
      await (TwoNPlugin.addListener as any)('twoNNavigateToIntercom', (event: any) => {
        // console.log('[2N] Navigation to intercom requested (pre-init):', event);
        
        // Trigger full initialization if needed
        if (!this.isFullyInitialized) {
          // console.log('[2N] Navigation requested - triggering full initialization');
          this.initializeAfterAuth().catch(error => {
            console.error('[2N] Failed to initialize on navigation request:', error);
          });
        }
        
        // Handle navigation
        this.notificationEventSubject.next({
          type: 'intercom',
          timestamp: Date.now(),
          reason: event.reason || 'navigation_request'
        });
      });

      // console.log('[2N] Basic navigation listener setup complete');
    } catch (error) {
      console.warn('[2N] Failed to setup basic navigation listener:', error);
    }
  }

  private async initializeInBackground(): Promise<boolean> {
    if (!this.isPlatformCompatible) {
      return false;
    }

    if (this.isInitializing) {
      // console.log('2N initialization already in progress');
      return false;
    }

    this.isInitializing = true;
    this.updateConnectionState('connecting');

    try {
      // console.log('Starting background initialization of 2N services...');

      // Fetch credentials from DynamoDB with fallback to secure storage
      // No Cognito session gate needed - fetchCredentialsWithFallback already falls back
      // to cached credentials from SecureStorage if DynamoDB is unavailable
      const credentials = await this.fetchCredentialsWithFallback('init');

      if (!credentials) {
        console.error('No 2N credentials found - cannot initialize');
        this.updateConnectionState('error', undefined, 'No credentials available');
        return false;
      }

      if (credentials.type !== 'device') {
        console.error('Invalid credential type - only device credentials are supported');
        this.updateConnectionState('error', undefined, 'Invalid credential type');
        return false;
      }

      // console.log('Found device credentials, initializing 2N plugin');
      // console.log('Using device ID:', credentials.username);

      // Initialize the plugin with device credentials
      await TwoNPlugin.initialize({
        username: credentials.username,  // deviceId
        password: credentials.password   // devicePassword
      });

      // Set up event listeners
      await this.initializeListeners();

      // Attempt auto-login with device credentials
      const loginSuccess = await this.autoLoginWithDeviceCredentials();

      // Start health check monitoring regardless of initial login success
      // This allows the health check to retry connection if it initially fails
      this.startConnectionHealthCheck();

      // Initialize BLE regardless of login success — BLE works independently via CoreSdk
      await this.initializeBLE();

      // console.log('Background initialization of 2N services completed');
      return true;
    } catch (error) {
      console.error('Error during background initialization:', error);
      this.updateConnectionState('error', undefined, String(error));
      return false;
    } finally {
      this.isInitializing = false;
    }
  }

  private isCallEvent(event: CallEvent | TwoNNotificationEvent): event is CallEvent {
    return 'message' in event || event.type === 'incoming' || event.type === 'connected' || 
           event.type === 'disconnected' || event.type === 'error' || event.type === 'ended';
  }

  private isNotificationEvent(event: CallEvent | TwoNNotificationEvent): event is TwoNNotificationEvent {
    return 'timestamp' in event;
  }

  private async initializeListeners() {
    if (this.isListenersInitialized) {
      // console.log('Event listeners already initialized, skipping duplicate setup');
      return;
    }
    
    try {
      // console.log('Initializing 2N event listeners...');
      
      // Remove existing listeners if any
      if (this.callEventListener) {
        await this.callEventListener.remove();
      }
      if (this.notificationEventListener) {
        await this.notificationEventListener.remove();
      }

      // Add call event listener
      this.callEventListener = await TwoNPlugin.addListener('twoNCallEvent', (event: CallEvent | TwoNNotificationEvent) => {
        if (this.isCallEvent(event)) {
          // console.log('[2N] ===== CALL EVENT RECEIVED =====');
          // console.log('[2N] Event type:', event.type);
          // console.log('[2N] Event message:', event.message);
          // console.log('[2N] Full event object:', JSON.stringify(event, null, 2));
          // console.log('[2N] Current time:', new Date().toISOString());
          // console.log('[2N] ===================================');
          
          this.callEventSubject.next(event);
          
          // Auto-navigate for incoming calls to ensure visibility
          if (event.type === 'incoming') {
            // console.log('[2N] Incoming call detected, triggering auto-navigation to intercom page');
            this.notificationEventSubject.next({
              type: 'intercom',
              timestamp: Date.now(),
              reason: 'incoming_call'
            });
          }
        }
      });

      // Add notification listener
      this.notificationEventListener = await TwoNPlugin.addListener('twoNNotificationReceived', (event: CallEvent | TwoNNotificationEvent) => {
        if (this.isNotificationEvent(event)) {
          // console.log('[2N] ===== NOTIFICATION RECEIVED =====');
          // console.log('[2N] Event type:', event.type);
          // console.log('[2N] Event timestamp:', event.timestamp);
          // console.log('[2N] Full event object:', JSON.stringify(event, null, 2));
          // console.log('[2N] Current time:', new Date().toISOString());
          // console.log('[2N] ====================================');
          
          this.notificationEventSubject.next(event);
          
          // Auto-navigate for intercom notifications
          if (event.type === 'intercom') {
            // console.log('[2N] Intercom notification received, triggering navigation to intercom page');
            // The notification itself triggers navigation through the app.component.ts subscription
          }
        }
      });
      
      // Add auto-answer call listener
      await (TwoNPlugin.addListener as any)('twoNCallAutoAnswered', (event: any) => {
        // console.log('[2N] ===== CALL AUTO-ANSWERED =====');
        // console.log('[2N] Event object:', JSON.stringify(event, null, 2));
        // console.log('[2N] Current time:', new Date().toISOString());
        // console.log('[2N] =================================');
        
        // Ensure we're authenticated since we're getting a call
        this.isAuthenticatedSubject.next(true);
        
        // Notify about the call event
        const callEvent: CallEvent = {
          type: 'connected',
          message: 'Call automatically answered'
        };
        
        // console.log('[2N] Sending call event notification for auto-answered call');
        this.callEventSubject.next(callEvent);
        
        // Navigate to intercom page through notification
        // console.log('[2N] Triggering navigation to intercom page due to auto-answered call');
        this.notificationEventSubject.next({
          type: 'intercom',
          timestamp: Date.now(),
          reason: 'auto_answered'
        });
      });
      
      // Add listener for direct navigation events
      await (TwoNPlugin.addListener as any)('twoNNavigateToIntercom', (event: any) => {
        // console.log('[2N] Navigation to intercom requested:', event);
        
        // Handle multiple call-related reasons, not just "call"
        const callRelatedReasons = ['call', 'call_retry', 'direct_notification', 'memory_warning', 'auto_answer'];
        
        if (event && event.reason && callRelatedReasons.includes(event.reason)) {
          // console.log(`[2N] Navigation requested due to call-related reason: ${event.reason}, navigating to intercom page`);
          
          // Navigate to intercom page through notification
          this.notificationEventSubject.next({
            type: 'intercom',
            timestamp: Date.now(),
            reason: event.reason
          });
        } else {
          // console.log(`[2N] Navigation requested with reason: ${event.reason || 'none'}, checking if we should navigate anyway`);
          
          // For safety, navigate anyway if we receive this event - it means something important is happening
          // console.log('[2N] Navigating to intercom for safety since we received a navigation event');
          this.notificationEventSubject.next({
            type: 'intercom',
            timestamp: Date.now(),
            reason: event.reason || 'unknown'
          });
        }
      });
      
      // Listen for login invalid notifications
      await TwoNPlugin.addListener('twoNLoginInvalid', (event: any) => {
        console.warn('Received 2N login invalid notification:', event);
        this.handleInvalidLogin();
      });

      // console.log('2N event listeners initialized successfully');
      this.isListenersInitialized = true;
    } catch (error) {
      console.error('Error initializing 2N listeners:', error);
      this.isListenersInitialized = false; // Reset flag on error
    }
  }
  
  /**
   * Attempt auto-login using device credentials from DynamoDB
   */
  async autoLoginWithDeviceCredentials(): Promise<boolean> {
    if (!this.isPlatformCompatible) {
      // console.log('2N Service - Platform not compatible, skipping auto-login');
      return false;
    }

    try {
      // console.log('Attempting auto-login with device credentials from DynamoDB...');

      // Fetch credentials from DynamoDB with fallback to secure storage
      // No Cognito session gate needed - fetchCredentialsWithFallback already falls back
      // to cached credentials from SecureStorage if DynamoDB is unavailable
      const credentials = await this.fetchCredentialsWithFallback('auto-login');

      if (!credentials) {
        console.error('No device credentials available for auto-login');
        this.updateConnectionState('error', undefined, 'No credentials available');
        return false;
      }

      if (credentials.type !== 'device') {
        console.error('Invalid credential type - only device credentials are supported');
        this.updateConnectionState('error', undefined, 'Invalid credential type');
        return false;
      }

      // Check if already logged in with the correct deviceId
      const currentStatus = await this.checkLoginStatus();
      if (currentStatus.isLoggedIn) {
        // Verify the native deviceId matches DynamoDB credentials
        if (currentStatus.deviceId === credentials.username) {
          // console.log('Already logged in to 2N with correct deviceId');
          return true;
        }
        // DeviceId mismatch - native has stale credentials, force re-login
        console.warn(`[2N] DeviceId mismatch: native=${currentStatus.deviceId}, DynamoDB=${credentials.username}. Re-logging in.`);
      }

      // console.log('Attempting anonymous device login with DynamoDB credentials...');
      // console.log('Device ID:', credentials.username);
      // console.log('Server URL:', credentials.url);

      const loginSuccess = await this.loginWithDevice(
        credentials.username,  // deviceId
        credentials.password,  // devicePassword
        credentials.url
      );

      if (loginSuccess) {
        // console.log('Auto-login with device credentials successful');
      } else {
        console.error('Auto-login with device credentials failed');
        // State already updated by loginWithDevice
      }

      return loginSuccess;
    } catch (error) {
      console.error('Error in auto-login with device credentials:', error);
      this.updateConnectionState('error', undefined, String(error));
      return false;
    }
  }

  // Handle invalid login state by attempting to reconnect
  private async handleInvalidLogin() {
    if (!this.isPlatformCompatible) {
      return;
    }

    const currentTime = Date.now();

    // Check if we're already in recovery mode
    if (this.isRecoveryInProgress) {
      // console.log('Login recovery already in progress, skipping');
      return;
    }

    // Check if we're in the cooldown period
    if (this.lastRecoveryAttempt > 0 &&
        currentTime - this.lastRecoveryAttempt < this.RECOVERY_COOLDOWN_MS) {
      // console.log('Login recovery in cooldown period, skipping');
      return;
    }

    // Check if we've exceeded the maximum number of attempts
    if (this.recoveryAttemptCount >= this.MAX_RECOVERY_ATTEMPTS) {
      // console.log(`Maximum recovery attempts (${this.MAX_RECOVERY_ATTEMPTS}) exceeded, giving up`);
      this.updateConnectionState('error', undefined, 'Maximum recovery attempts exceeded');
      this.showToast('Unable to connect to intercom service. Please try again later.');
      return;
    }

    // First check if we have a valid Cognito session
    const session = this.authService.getSession();
    if (!session?.isValid()) {
      // console.log('No valid Cognito session found - skipping login recovery');
      this.updateConnectionState('disconnected', undefined, 'No valid Cognito session');
      return;
    }

    try {
      // Set recovery flag to prevent concurrent recovery attempts
      this.isRecoveryInProgress = true;
      this.lastRecoveryAttempt = currentTime;
      this.recoveryAttemptCount++;

      // Set reconnecting state
      this.updateConnectionState('reconnecting');

      // Wait a moment before attempting recovery
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Fetch credentials from DynamoDB with fallback to secure storage
      const credentials = await this.fetchCredentialsWithFallback('recovery');

      let success = false;

      if (credentials && credentials.type === 'device') {
        success = await this.loginWithDevice(
          credentials.username,
          credentials.password,
          credentials.url
        );
      } else {
        console.error('No valid device credentials available for recovery');
        this.updateConnectionState('error', undefined, 'No credentials available');
      }

      // Check if the recovery was successful
      if (success) {
        // console.log('Recovery login successful');
        // Reset attempt counter on success
        this.recoveryAttemptCount = 0;

        // Initialize BLE now that we are authenticated
        // console.log('[2N] Recovery login successful, initializing BLE...');
        this.initializeBLE().catch(error => {
          console.error('[2N] Failed to initialize BLE after recovery:', error);
        });
      } else {
        console.warn('Recovery login failed');
        // State already updated by loginWithDevice
      }
    } catch (error) {
      console.error('Failed to recover from invalid login state:', error);
      this.updateConnectionState('error', undefined, String(error));
    } finally {
      // Clear recovery flag when done
      this.isRecoveryInProgress = false;
    }
  }

  /**
   * Manually reconnect to 2N services with fresh credentials
   * Used when credentials are updated
   */
  async manualReconnect(): Promise<boolean> {
    // Wait for platform check to complete (max 5 seconds)
    const maxWait = 5000;
    const startTime = Date.now();
    while (!this.platformCheckCompleted && (Date.now() - startTime) < maxWait) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    if (!this.isPlatformCompatible) {
      console.log('2N Service - Platform not compatible, skipping manual reconnect');
      return false;
    }

    console.log('Manual reconnect to 2N services requested');
    this.updateConnectionState('reconnecting');

    try {
      // First check if we have a valid Cognito session
      const session = this.authService.getSession();
      if (!session?.isValid()) {
        // console.log('No valid Cognito session found - skipping manual reconnect');
        this.updateConnectionState('error', undefined, 'No valid Cognito session');
        return false;
      }

      // Fetch credentials from DynamoDB with fallback to secure storage
      const credentials = await this.fetchCredentialsWithFallback('reconnect');

      if (!credentials) {
        console.error('No credentials available for manual reconnect');
        this.updateConnectionState('error', undefined, 'No credentials available');
        return false;
      }

      if (credentials.type !== 'device') {
        console.error('Invalid credential type - only device credentials are supported');
        this.updateConnectionState('error', undefined, 'Invalid credential type');
        return false;
      }

      // Initialize the plugin with device credentials
      // initialize() reports isActive whenever the plugin is initialized; that is not a
      // My2N session. Login state comes from checkLoginStatus()/loginWithDevice below.
      await TwoNPlugin.initialize({
        username: credentials.username,  // deviceId
        password: credentials.password   // devicePassword
      });

      // Set up event listeners
      await this.initializeListeners();

      // Double-check login status before attempting login
      const currentStatus = await this.checkLoginStatus();
      if (currentStatus.isLoggedIn) {
        console.log('Already logged in to 2N, skipping login attempt');
        // State already updated by checkLoginStatus

        // Initialize BLE functionality
        await this.initializeBLE();
        // Start health check monitoring
        this.startConnectionHealthCheck();
        return true;
      }

      // Attempt login with device credentials
      const loginSuccess = await this.loginWithDevice(
        credentials.username,
        credentials.password,
        credentials.url
      );

      if (loginSuccess) {
        // console.log('Manual reconnect successful');

        // Initialize BLE functionality
        // console.log('Authentication successful, initializing BLE functionality...');
        await this.initializeBLE();
        // Start health check monitoring
        this.startConnectionHealthCheck();

        return true;
      } else {
        console.error('Manual reconnect login failed');
        // State already updated by loginWithDevice
        return false;
      }
    } catch (error) {
      console.error('Error during manual reconnect:', error);
      this.updateConnectionState('error', undefined, String(error));
      return false;
    }
  }

  /**
   * Login using device credentials (anonymous device login)
   * This is the ONLY supported login method
   * @param deviceId Device ID
   * @param devicePassword Device password
   * @param url Optional server URL (defaults to https://my2n.com)
   */
  async loginWithDevice(deviceId: string, devicePassword: string, url?: string): Promise<boolean> {
    if (!this.isPlatformCompatible) {
      // console.log('2N Service - Platform not compatible, skipping device login');
      return false;
    }

    // Set connecting state before attempting login
    this.updateConnectionState('connecting');

    try {
      // console.log('Attempting 2N anonymous device login...');
      // console.log('DeviceId:', deviceId);
      // console.log('Server URL:', url || 'https://my2n.com');

      // Call the plugin method with proper error handling
      let result: { success: boolean; message: string; username?: string; deviceId?: string };

      try {
        const pluginResult = await TwoNPlugin.loginWithDevice({
          username: deviceId,
          password: devicePassword,
          url: url || 'https://my2n.com'
        }) as { success: boolean; message: string; username?: string; deviceId?: string } | void;

        // Handle the case where the plugin might return void (web platform)
        if (!pluginResult || typeof pluginResult !== 'object' || !('success' in pluginResult)) {
          console.warn('Plugin returned invalid result, assuming web platform or plugin error');
          this.updateConnectionState('error', undefined, 'Plugin returned invalid result');
          return false;
        }

        result = pluginResult;
      } catch (pluginError: any) {
        console.error('Plugin method call failed:', pluginError);
        // If we're on web platform, the plugin will throw an error
        if (pluginError.message && pluginError.message.includes('not available on web')) {
          // console.log('Running on web platform - 2N functionality not available');
          this.updateConnectionState('disconnected', undefined, 'Not available on web');
          return false;
        }
        throw pluginError;
      }

      if (result.success) {
        // console.log('Anonymous device login successful');
        this.updateConnectionState('connected', deviceId);

        // Initialize BLE now that we are authenticated
        // console.log('[2N] Authentication successful, initializing BLE...');
        this.initializeBLE().catch(error => {
          console.error('[2N] Failed to initialize BLE after authentication:', error);
        });

        return true;
      } else {
        // Check if the error is "alreadyLoggedIn" - this means we're actually connected
        if (result.message && result.message.toLowerCase().includes('alreadyloggedin')) {
          console.log('2N reports already logged in - treating as success');
          this.updateConnectionState('connected', deviceId);

          // Initialize BLE since we are authenticated
          this.initializeBLE().catch(error => {
            console.error('[2N] Failed to initialize BLE after alreadyLoggedIn:', error);
          });

          return true;
        }

        console.warn('Anonymous device login failed:', result.message);
        this.updateConnectionState('error', undefined, result.message || 'Login failed');
        return false;
      }
    } catch (error) {
      console.error('Error logging in to 2N with device credentials:', error);
      this.updateConnectionState('error', undefined, String(error));
      return false;
    }
  }

  async checkLoginStatus(): Promise<TwoNLoginStatus> {
    if (!this.isPlatformCompatible) {
      // console.log('2N Service - Platform not compatible, returning logged out status');
      return { isLoggedIn: false, state: 'Platform not compatible' };
    }

    try {
      // console.log('Checking 2N login status');
      const status = await TwoNPlugin.isLoggedIn() as TwoNLoginStatus;
      // console.log(`Login status: ${status.isLoggedIn}, state: ${status.state}, deviceId: ${status.deviceId}`);

      if (status.isLoggedIn && status.deviceId) {
        // Filter out invalid deviceIds
        if (!status.deviceId.includes('@') && status.deviceId.length > 3) {
          // console.log(`Valid device ID found: ${status.deviceId}`);
          this.updateConnectionState('connected', status.deviceId);
        } else {
          // console.log(`Invalid device ID format: ${status.deviceId}`);
          this.updateConnectionState('disconnected', undefined, 'Invalid device ID format');
          status.isLoggedIn = false;
        }
      } else if (status.isLoggedIn && !status.deviceId) {
        // We claim to be logged in but have no device ID - report as not logged in
        console.warn('Login status reports logged in but no device ID available');
        this.updateConnectionState('disconnected', undefined, 'No device ID available');
        status.isLoggedIn = false;
      } else {
        // Plugin reports NOT logged in - update state to disconnected
        this.updateConnectionState('disconnected');
      }

      return status;
    } catch (error) {
      console.error('Error checking login status:', error);
      this.updateConnectionState('error', undefined, 'Error checking status');
      return { isLoggedIn: false, state: 'Error checking status' };
    }
  }

  async logout(): Promise<void> {
    // Stop health check monitoring
    this.stopConnectionHealthCheck();

    if (!this.isPlatformCompatible) {
      // console.log('2N Service - Platform not compatible, updating local state only');
      this.updateConnectionState('disconnected');
      return;
    }

    try {
      await TwoNPlugin.logout();
      this.updateConnectionState('disconnected');
    } catch (error) {
      console.error('Error logging out from 2N:', error);
      throw error;
    }
  }

  async startVideoCall(deviceId: string): Promise<void> {
    if (!this.isPlatformCompatible) {
      throw new Error('2N services not available on this platform');
    }

    try {
      await TwoNPlugin.startVideoCall({ deviceId });
    } catch (error) {
      console.error('Error starting video call:', error);
      throw error;
    }
  }

  async endVideoCall(): Promise<void> {
    try {
      await TwoNPlugin.endVideoCall();
    } catch (error) {
      console.error('Error ending video call:', error);
      throw error;
    }
  }

  async answerIncomingCall(): Promise<{ success: boolean; message: string }> {
    try {
      // console.log('[2N] Answering incoming call');
      const result = await (TwoNPlugin as any).answerIncomingCall();
      // console.log('[2N] Incoming call answered successfully:', result);
      return result;
    } catch (error) {
      console.error('Error answering incoming call:', error);
      throw error;
    }
  }

  async declineIncomingCall(): Promise<{ success: boolean; message: string }> {
    try {
      // console.log('[2N] Declining incoming call');
      const result = await (TwoNPlugin as any).declineIncomingCall();
      // console.log('[2N] Incoming call declined successfully:', result);
      return result;
    } catch (error) {
      console.error('Error declining incoming call:', error);
      throw error;
    }
  }

  async unlockDoor(deviceId: string): Promise<void> {
    if (!this.isPlatformCompatible) {
      throw new Error('2N services not available on this platform');
    }

    try {
      // console.log('[2N] Attempting to unlock door for device:', deviceId);
      
      // First try BLE unlock if available and we have an active device
      const isBleReady = this.bleService.isBleReady;
      const hasActiveDevice = this.bleService.hasActiveDevice;
      
      if (isBleReady && hasActiveDevice) {
        // console.log('[2N] Attempting BLE unlock first...');
        try {
          const bleSuccess = await this.bleService.unlockDoor();
          if (bleSuccess) {
            // console.log('[2N] Door unlocked successfully via BLE');
            return;
          } else {
            console.warn('[2N] BLE unlock failed, falling back to SIP DTMF');
          }
        } catch (bleError) {
          console.warn('[2N] BLE unlock error, falling back to SIP DTMF:', bleError);
        }
      } else {
        // console.log('[2N] BLE not available, using SIP DTMF unlock');
      }
      
      // Fallback to SIP DTMF unlock
      // console.log('[2N] Using SIP DTMF unlock method');
      await (TwoNPlugin as any).unlockDoor({ deviceId });
      // console.log('[2N] Door unlock command sent successfully via SIP DTMF');
      
    } catch (error: any) {
      console.error('[2N] Failed to unlock door:', error);
      
      // Provide better error messages based on the specific error
      if (error.errorMessage && error.errorMessage.includes('No active call')) {
        throw new Error('Door unlock requires an active call. Please answer an incoming call first.');
      } else if (error.errorMessage && error.errorMessage.includes('Call is not connected')) {
        throw new Error('Door unlock requires a connected call. Please ensure the call is active.');
      } else if (error.errorMessage && error.errorMessage.includes('call may not support DTMF')) {
        throw new Error('The current call does not support DTMF codes. Door unlock may not be available.');
      }
      
      throw error;
    }
  }

  /**
   * Get available DTMF codes for a specific device
   * @param deviceId The device ID to get DTMF codes for
   */
  async getDeviceDTMFCodes(deviceId: string): Promise<{ dtmfCodes: any[] }> {
    try {
      // console.log('[2N] Getting DTMF codes for device:', deviceId);
      const result = await (TwoNPlugin as any).getDeviceDTMFCodes({ deviceId });
      // console.log('[2N] Retrieved DTMF codes:', result);
      return result;
    } catch (error) {
      console.error('Error getting device DTMF codes:', error);
      throw error;
    }
  }

  /**
   * Send a specific DTMF code
   * @param dtmfCode The DTMF code to send
   * @param name Optional name/description of the DTMF code
   */
  async sendDTMF(dtmfCode: string, name?: string): Promise<{ success: boolean; message: string }> {
    try {
      // console.log('[2N] Sending DTMF code:', dtmfCode, name ? `(${name})` : '');
      const result = await (TwoNPlugin as any).sendDTMF({ dtmfCode, name });
      // console.log('[2N] DTMF code sent successfully:', result);
      return result;
    } catch (error) {
      console.error('Error sending DTMF code:', error);
      throw error;
    }
  }

  async toggleAudioOutput(): Promise<{ currentOutput: string }> {
    try {
      return await TwoNPlugin.toggleAudioOutput();
    } catch (error) {
      console.error('Error toggling audio output:', error);
      throw error;
    }
  }

  async toggleMicrophone(enabled: boolean): Promise<void> {
    try {
      await TwoNPlugin.toggleMicrophone({ enabled });
    } catch (error) {
      console.error('Error toggling microphone:', error);
      throw error;
    }
  }

  async getVideoStream(): Promise<{ streamUrl: string }> {
    // For 2N auto-answered calls, we don't need an RTSP stream URL
    // The video is handled natively by the 2N SDK
    // console.log('Getting video stream for current call');
    return { streamUrl: '' };
  }

  /**
   * Set up the video container for the 2N intercom video
   * This method configures the native plugin to use a specific DOM element for video rendering
   * @param containerId The ID of the HTML element to use for video display
   */
  async setupVideoContainer(containerId: string): Promise<boolean> {
    try {
      // console.log(`Setting up video container with ID: ${containerId}`);
      
      const result = await (TwoNPlugin as any).setupVideoView({
        viewId: containerId
      });
      
      // console.log('Video container setup result:', result);
      return result.success;
    } catch (error) {
      console.error('Error setting up video container:', error);
      return false;
    }
  }

  /**
   * Prepare for incoming video calls by initializing the video view
   * This should be called when the intercom page loads
   * @param containerId The HTML element ID to use for video display
   */
  async prepareForVideoCall(containerId: string): Promise<boolean> {
    try {
      // First try to set up the video container
      const containerResult = await this.setupVideoContainer(containerId);
      
      // Then check login status to ensure we're ready for calls
      const loginStatus = await this.checkLoginStatus();
      if (!loginStatus.isLoggedIn) {
        console.warn('Not logged in to 2N service - attempting auto-login');
        await this.autoLoginWithDeviceCredentials();
      }
      
      // console.log('Ready for 2N video calls');
      return true;
    } catch (error) {
      console.error('Error preparing for video calls:', error);
      return false;
    }
  }

  async cleanupVideoViews(): Promise<boolean> {
    try {
      // console.log('Attempting to clean up any native video views');
      
      // First try to end any active call
      try {
        await this.endVideoCall();
        // console.log('Successfully ended any active calls');
      } catch (error) {
        // Ignore errors here - might just mean no active call
        // console.log('No active call to end or error ending call:', error);
      }
      
      // Call the explicit cleanupVideoView method
      try {
        const result = await (TwoNPlugin as any).cleanupVideoView();
        // console.log('Video view cleanup result:', result);
        return result.success;
      } catch (error) {
        console.warn('Error calling cleanupVideoView method:', error);
        
        // As a fallback, try toggling video capture off
        if ('toggleVideo' in TwoNPlugin) {
          try {
            // Turn video off
            await (TwoNPlugin as any).toggleVideo({ enabled: false });
            // console.log('Successfully turned off video capture');
            return true;
          } catch (error) {
            console.warn('Error toggling video off:', error);
          }
        }
      }
      
      // console.log('Completed all video cleanup steps');
      return true;
    } catch (error) {
      console.error('Error during video view cleanup:', error);
      return false;
    }
  }

  async cleanup(): Promise<void> {
    // console.log('Cleaning up 2N service...');

    // Stop health check monitoring
    this.stopConnectionHealthCheck();

    try {
      if (this.isPlatformCompatible) {
        // Remove individual listeners first to prevent crashes
        if (this.callEventListener) {
          await this.callEventListener.remove();
          this.callEventListener = null;
        }

        if (this.notificationEventListener) {
          await this.notificationEventListener.remove();
          this.notificationEventListener = null;
        }

        // Then remove all remaining listeners
        await (TwoNPlugin as any).removeAllListeners();

        // Clean up video views
        await this.cleanupVideoViews();

        // Clean up BLE listeners
        this.bleService.cleanupListeners();
      } else {
        // console.log('2N Service - Platform not compatible, skipping native cleanup');
      }

      // Always reset connection state
      this.updateConnectionState('disconnected');

      // console.log('2N service cleanup complete');
    } catch (error) {
      console.error('Error during 2N service cleanup:', error);
      // Don't throw - cleanup should be resilient
    }
  }

  /**
   * Initialize BLE functionality - only called after successful authentication
   */
  private async initializeBLE(): Promise<void> {
    try {
      // console.log('[2N] Starting BLE initialization...');
      
      // Initialize BLE
      const bleInitialized = await this.bleService.initializeBLE();
      if (!bleInitialized) {
        console.warn('[2N] BLE initialization failed, continuing without BLE functionality');
        return;
      }

      // console.log('[2N] BLE initialized successfully, importing credentials...');
      
      // Import and activate device credentials
      const credentialsImported = await this.bleService.importAndActivateDeviceCredentials();
      if (credentialsImported) {
        // console.log('[2N] BLE device credentials imported and activated successfully');

        // Start periodic credential refresh (every 24 hours)
        console.log('[2N] Starting periodic credential refresh...');
        this.bleService.startPeriodicCredentialRefresh();
      } else {
        console.warn('[2N] BLE credential import failed, BLE functionality may be limited');
      }

      // Setup event listeners for BLE auto-unlock functionality
      this.setupBleEventListeners();
      
    } catch (error) {
      console.error('[2N] Error during BLE initialization:', error);
    }
  }

  /**
   * Initialize BLE functionality on demand - can be called after authentication
   */
  public async initializeBLEOnDemand(): Promise<boolean> {
    if (!this.isPlatformCompatible) {
      // console.log('2N Service - Platform not compatible, cannot initialize BLE');
      return false;
    }

    try {
      // console.log('[2N] Starting on-demand BLE initialization...');
      await this.initializeBLE();
      return true;
    } catch (error) {
      console.error('[2N] Error during on-demand BLE initialization:', error);
      return false;
    }
  }

  private setupBleEventListeners(): void {
    // console.log('[2N] Setting up BLE event listeners for auto-unlock');

    // Listen for device scanning events
    (TwoNPlugin as any).addListener('bleDeviceScanned', (event: any) => {
      // console.log('[2N] BLE Device scanned:', event);
      
      // Log important details for debugging
      if (event.paired && event.activated) {
        // console.log(`[2N] Paired and activated device detected: ${event.name} (RSSI: ${event.rssi})`);
      }
    });

    // Listen for successful auto-unlock attempts
    (TwoNPlugin as any).addListener('bleAutoUnlockAttempt', (event: any) => {
      // console.log('[2N] BLE Auto-unlock attempt:', event);
      // You could show a user notification here
    });

    // Listen for successful door opening
    (TwoNPlugin as any).addListener('bleAutoUnlockSuccess', (event: any) => {
      // console.log('[2N] BLE Auto-unlock SUCCESS:', event);
      // Track phone as key access
      this.analyticsService.trackWidgetInteraction('intercom', 'phone_as_key_unlock');
      // Show success notification to user
      this.showToast('Door unlocked via Bluetooth!');
    });

    // Listen for authentication events
    (TwoNPlugin as any).addListener('bleAuthEvent', (event: any) => {
      // console.log('[2N] BLE Auth event:', event);

      if (event.event === 'doorOpened') {
        // console.log('[2N] Door successfully opened via BLE');
        // Track phone as key access (BLE auth method)
        this.analyticsService.trackWidgetInteraction('intercom', 'phone_as_key_access');
        this.showToast('Door opened successfully!');
      } else if (event.event === 'authFailure') {
        // console.log('[2N] BLE authentication failed:', event.reason);
      }
    });

    // Listen for device pairing events
    (TwoNPlugin as any).addListener('bleDevicePaired', (event: any) => {
      // console.log('[2N] BLE Device paired successfully:', event);
      // Track Bluetooth connected
      this.analyticsService.trackWidgetInteraction('intercom', 'bluetooth_connected');
      this.showToast('Device paired successfully!');
    });

    // Listen for device disconnection events
    (TwoNPlugin as any).addListener('bleDeviceDisconnected', (event: any) => {
      // console.log('[2N] BLE Device disconnected:', event);
    });

    // console.log('[2N] BLE event listeners configured');
  }

  private showToast(message: string): void {
    // Implement toast notification for user feedback
    // console.log(`[2N] Toast: ${message}`);
    // You can integrate with Ionic toast controller here
  }

  /**
   * Get the BLE service for direct access to BLE functionality
   */
  get ble(): TwoNBleService {
    return this.bleService;
  }

  /**
   * Enable BLE card mode for auto-unlock functionality
   */
  async enableBleCardMode(): Promise<{ success: boolean; message: string; cardMode: { enabled: boolean; threshold: number } }> {
    if (!this.isPlatformCompatible) {
      return {
        success: false,
        message: '2N services not available on this platform',
        cardMode: { enabled: false, threshold: -65 }
      };
    }

    try {
      const result = await (TwoNPlugin as any).enableBleCardMode();
      return result;
    } catch (error) {
      console.error('[2N] Error enabling BLE card mode:', error);
      return {
        success: false,
        message: `Failed to enable BLE card mode: ${error}`,
        cardMode: { enabled: false, threshold: -65 }
      };
    }
  }

  /**
   * Disable BLE card mode to prevent auto-unlock (but keep credentials)
   */
  async disableBleCardMode(): Promise<{ success: boolean; message: string; cardMode: { enabled: boolean; threshold: number } }> {
    try {
      const result = await (TwoNPlugin as any).disableBleCardMode();
      return result;
    } catch (error) {
      console.error('[2N] Error disabling BLE card mode:', error);
      return {
        success: false,
        message: `Failed to disable BLE card mode: ${error}`,
        cardMode: { enabled: true, threshold: -65 }
      };
    }
  }

  /**
   * Get the current BLE card mode status
   */
  async getBleCardModeStatus(): Promise<{ success: boolean; cardMode: { enabled: boolean; threshold: number } }> {
    try {
      if (!this.isPlatformCompatible) {
        return {
          success: true,
          cardMode: { enabled: false, threshold: -65 }
        };
      }

      const result = await (TwoNPlugin as any).getBleCardModeStatus();
      return result;
    } catch (error) {
      console.error('[2N Service] Error getting BLE card mode status:', error);
      return {
        success: false,
        cardMode: { enabled: false, threshold: -65 }
      };
    }
  }

  /**
   * Safely store credentials with deduplication
   */
  private async safeStoreTwoNCredentials(credentials: TwoNCredentials): Promise<boolean> {
    if (this.isCredentialsStoring) {
      // console.log('Credentials already being stored, skipping duplicate call');
      return true;
    }
    
    this.isCredentialsStoring = true;
    try {
      const result = await this.credentialsService.storeTwoNCredentials(credentials);
      // console.log('Credentials stored safely');
      return result;
    } finally {
      this.isCredentialsStoring = false;
    }
  }
} 